import {
  BlastWindowState,
  DeliveryTicketStatus,
  DepartmentKpiTemplate,
  LossBucket,
  MaterialBasis,
  OperationEventKind,
  OperationEventSeverity,
  OperationRequestPriority,
  OperationRequestSlaState,
  OperationRequestStatus,
  OperationTriggerMode,
  OperationTriggerPriority,
  TaskSource,
  TaskUnassignedReason,
} from 'types/operationsEnums.interface'
import { OpsCoreState, OpsEventKind, OpsEventSeverity, OpsTaskSource } from 'types/operationsControl.interface'
import { OpsRequestPriority, OpsRequestSlaState, OpsRequestStatus } from 'types/opsRequests.interface'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { OpsLossBucket } from 'types/opsAnalytics.interface'

export const TASK_SOURCE_TO_UI: { [k: number]: OpsTaskSource } = {
  [TaskSource.Manual]: 'MANUAL',
  [TaskSource.PlanScheduler]: 'PLAN',
  [TaskSource.ShiftProgram]: 'SHIFT_PROGRAM',
  [TaskSource.TriggerCompletion]: 'TRIGGER',
  [TaskSource.TriggerScheduled]: 'TRIGGER',
  [TaskSource.TriggerThreshold]: 'TRIGGER',
  [TaskSource.IssueTrigger]: 'TRIGGER',
  [TaskSource.Request]: 'REQUEST',
}

export const TASK_SOURCE_TO_API: { [k in OpsTaskSource]: TaskSource } = {
  MANUAL: TaskSource.Manual,
  PLAN: TaskSource.PlanScheduler,
  SHIFT_PROGRAM: TaskSource.ShiftProgram,
  TRIGGER: TaskSource.TriggerCompletion,
  REQUEST: TaskSource.Request,
}

export const EVENT_KIND_TO_UI: { [k: number]: OpsEventKind } = {
  [OperationEventKind.ShiftStart]: 'shift_start',
  [OperationEventKind.ShiftProgramRun]: 'shift_program_run',
  [OperationEventKind.TaskGenerated]: 'task_generated',
  [OperationEventKind.TaskStarted]: 'task_started',
  [OperationEventKind.TaskCompleted]: 'task_completed',
  [OperationEventKind.TaskBlocked]: 'task_blocked',
  [OperationEventKind.TriggerFired]: 'trigger_fired',
  [OperationEventKind.AlertRaised]: 'alert_raised',
  [OperationEventKind.MachineDown]: 'machine_down',
  [OperationEventKind.MachineUp]: 'machine_up',
  [OperationEventKind.BookingCreated]: 'booking_created',
  [OperationEventKind.PlanProgress]: 'plan_progress',
  [OperationEventKind.Incident]: 'incident',
  [OperationEventKind.Request]: 'request',
}

export const EVENT_SEVERITY_TO_UI: { [k: number]: OpsEventSeverity } = {
  [OperationEventSeverity.Critical]: 'critical',
  [OperationEventSeverity.Warning]: 'warning',
  [OperationEventSeverity.Info]: 'info',
  [OperationEventSeverity.Success]: 'success',
}

export const REQUEST_STATUS_TO_UI: { [k: number]: OpsRequestStatus } = {
  [OperationRequestStatus.Open]: 'open',
  [OperationRequestStatus.Acknowledged]: 'acknowledged',
  [OperationRequestStatus.InProgress]: 'in_progress',
  [OperationRequestStatus.Done]: 'done',
  [OperationRequestStatus.Declined]: 'declined',
}

export const REQUEST_STATUS_TO_API: { [k in OpsRequestStatus]: OperationRequestStatus } = {
  open: OperationRequestStatus.Open,
  acknowledged: OperationRequestStatus.Acknowledged,
  'in_progress': OperationRequestStatus.InProgress,
  done: OperationRequestStatus.Done,
  declined: OperationRequestStatus.Declined,
}

export const REQUEST_PRIORITY_TO_UI: { [k: number]: OpsRequestPriority } = {
  [OperationRequestPriority.Low]: 'low',
  [OperationRequestPriority.Normal]: 'normal',
  [OperationRequestPriority.High]: 'high',
  [OperationRequestPriority.Critical]: 'critical',
}

export const REQUEST_PRIORITY_TO_API: { [k in OpsRequestPriority]: OperationRequestPriority } = {
  low: OperationRequestPriority.Low,
  normal: OperationRequestPriority.Normal,
  high: OperationRequestPriority.High,
  critical: OperationRequestPriority.Critical,
}

export const REQUEST_SLA_TO_UI: { [k: number]: OpsRequestSlaState } = {
  [OperationRequestSlaState.OnTime]: 'on_time',
  [OperationRequestSlaState.DueSoon]: 'due_soon',
  [OperationRequestSlaState.Overdue]: 'overdue',
}

export const KPI_TEMPLATE_TO_UI: { [k: number]: IOpsDepartment['kpiTemplate'] } = {
  [DepartmentKpiTemplate.Mining]: 'mining',
  [DepartmentKpiTemplate.Plant]: 'plant',
  [DepartmentKpiTemplate.Maintenance]: 'maintenance',
  [DepartmentKpiTemplate.Camp]: 'camp',
  [DepartmentKpiTemplate.Security]: 'security',
  [DepartmentKpiTemplate.Custom]: 'custom',
}

export const KPI_TEMPLATE_TO_API: { [k in IOpsDepartment['kpiTemplate']]: DepartmentKpiTemplate } = {
  mining: DepartmentKpiTemplate.Mining,
  plant: DepartmentKpiTemplate.Plant,
  maintenance: DepartmentKpiTemplate.Maintenance,
  camp: DepartmentKpiTemplate.Camp,
  security: DepartmentKpiTemplate.Security,
  custom: DepartmentKpiTemplate.Custom,
}

export const TRIGGER_MODE_TO_UI: { [k: number]: 'COMPLETION' | 'SCHEDULED' | 'THRESHOLD' } = {
  [OperationTriggerMode.Completion]: 'COMPLETION',
  [OperationTriggerMode.Scheduled]: 'SCHEDULED',
  [OperationTriggerMode.Threshold]: 'THRESHOLD',
}

export const TRIGGER_PRIORITY_TO_UI: { [k: number]: 'low' | 'normal' | 'high' | 'critical' } = {
  [OperationTriggerPriority.Low]: 'low',
  [OperationTriggerPriority.Normal]: 'normal',
  [OperationTriggerPriority.High]: 'high',
  [OperationTriggerPriority.Critical]: 'critical',
}

export const LOSS_BUCKET_TO_UI: { [k: number]: OpsLossBucket | 'UNATTRIBUTED' } = {
  [LossBucket.WeatherBlastHold]: 'WEATHER_BLAST_HOLD',
  [LossBucket.BlockedCrossDept]: 'BLOCKED_CROSS_DEPT',
  [LossBucket.PlannedMaint]: 'PLANNED_MAINT',
  [LossBucket.Breakdown]: 'BREAKDOWN',
  [LossBucket.NoOperator]: 'NO_OPERATOR',
  [LossBucket.NoTask]: 'NO_TASK',
  [LossBucket.Productive]: 'PRODUCTIVE',
  [LossBucket.Unattributed]: 'UNATTRIBUTED',
}

export const MATERIAL_BASIS_TO_UI: { [k: number]: 'DRY' | 'WET' } = {
  [MaterialBasis.Dry]: 'DRY',
  [MaterialBasis.Wet]: 'WET',
}

export const MATERIAL_BASIS_TO_API: { [k in 'DRY' | 'WET']: MaterialBasis } = {
  DRY: MaterialBasis.Dry,
  WET: MaterialBasis.Wet,
}

export const DELIVERY_STATUS_TO_UI: { [k: number]: string } = {
  [DeliveryTicketStatus.Draft]: 'DRAFT',
  [DeliveryTicketStatus.Arrived]: 'ARRIVED',
  [DeliveryTicketStatus.InProgress]: 'IN_PROGRESS',
  [DeliveryTicketStatus.Completed]: 'COMPLETED',
  [DeliveryTicketStatus.Finalized]: 'FINALIZED',
}

export const DELIVERY_STATUS_TO_API: { [k: string]: DeliveryTicketStatus } = {
  DRAFT: DeliveryTicketStatus.Draft,
  ARRIVED: DeliveryTicketStatus.Arrived,
  IN_PROGRESS: DeliveryTicketStatus.InProgress,
  COMPLETED: DeliveryTicketStatus.Completed,
  FINALIZED: DeliveryTicketStatus.Finalized,
}

export const BLAST_STATE_TO_UI: { [k: number]: 'scheduled' | 'active' | 'cleared' } = {
  [BlastWindowState.Scheduled]: 'scheduled',
  [BlastWindowState.Active]: 'active',
  [BlastWindowState.Cleared]: 'cleared',
}

export const UNASSIGNED_REASON_LABEL: { [k: number]: string } = {
  [TaskUnassignedReason.NoEligibleWorkers]: 'No eligible workers',
  [TaskUnassignedReason.NoAvailableMachines]: 'No available machines',
  [TaskUnassignedReason.CapabilityMismatch]: 'Capability mismatch',
}

export const TASK_STATUS_TO_CORE: { [k: number]: OpsCoreState } = {
  0: 'DRAFT',
  1: 'ACTIVE',
  2: 'PAUSED',
  3: 'COMPLETED',
  4: 'BLOCKED',
  5: 'PAUSED',
  6: 'PAUSED',
  7: 'CANCELLED',
}
